import { useEffect } from 'react';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Box } from '~/eds/src/components/Box/Box';
import { LoadingContent } from '~/eds/src/components/LoadingContent/LoadingContent';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import { api } from '~/redux/api/createApi';
import type { Stage } from '~/redux/api/methods/k2/getStages';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { StageTasks } from './StageTasks';
import { getTaskDetailsPanelId } from './task-details/TaskDetailsPanel';
import { TasksAccordionHeader } from './TasksAccordionHeader';

export const TasksTab = () => {
  const { params, navigate } = useRouting();
  const ticketId = params.ticketId;
  const taskId = params.taskId;
  const { openPanel, closePanel, openPanelId } = usePanelContext();

  useEffect(() => {
    if (taskId) {
      openPanel(getTaskDetailsPanelId(taskId));
    }
  }, [taskId, openPanel]);

  const { data: stages = [], isLoading } = api.endpoints.getStagesK2.useQuery({
    ticketId: ticketId!,
  });
  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isError: isTasksError,
  } = api.endpoints.listTasksK2.useQuery({
    ticketId: ticketId!,
  });

  useEffect(() => {
    if (!taskId || !ticketId || isLoadingTasks || isTasksError) {
      return;
    }

    const isValidTaskId = tasks.some((task) => task.id === taskId);
    if (!isValidTaskId) {
      if (openPanelId) {
        closePanel(openPanelId);
      }
      const tasksPath = `${RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(':ticketId', ticketId)}/tasks`;
      navigate(tasksPath, { replace: true });
    }
  }, [
    taskId,
    ticketId,
    isLoadingTasks,
    isTasksError,
    tasks,
    openPanelId,
    closePanel,
    navigate,
  ]);

  const stageAccordionItems = [...stages]
    .sort((a, b) => a.order - b.order)
    .map((stage: Stage) => ({
      title: stage.name,
      summary: <TasksAccordionHeader stage={stage} />,
      content: (
        <StageTasks
          tasks={tasks.filter((task) => task.stage_id === stage.id)}
          isLoading={isLoadingTasks}
        />
      ),
      isExpanded: stage.is_current,
      data: stage,
    }));

  return (
    <Box>
      {isLoading ? (
        <LoadingContent />
      ) : (
        <Accordion<Stage>
          items={stageAccordionItems}
          itemGap="s"
          border="none"
        />
      )}
    </Box>
  );
};
